/**
 * Schäden — Inventar 2.0, S7b.
 *
 * The section was already a <ul> of <li> cards; what made it read as legacy was
 * the chrome (hand-written hex that only worked in light mode because
 * index.css overrode every colour back) and the ordering: the photo strip sat
 * between the description and the facts, so the three things an operator
 * actually scans for — status, date, cost — were scattered.
 *
 * SEMANTICS UNCHANGED. Same four statuses, same German labels, same allowed
 * transitions, same select gate (a select appears only where more than one
 * option exists, i.e. never on repaired / written_off), same handler, same
 * client-side filter and sort, same PhotoGallery wiring.
 *
 * THE <h2> STAYS A DIRECT CHILD OF THE <section>. The S6a suites resolve this
 * surface as screen.getByRole("heading", { name: "Schäden" }).parentElement,
 * so the section itself has to be that parent. That is also why this file does
 * not reuse DetailCard, which puts its heading inside a header row for the
 * action slot — a slot Schäden has no use for, since "Schaden melden" lives on
 * the assignment rows.
 *
 * NOT FIXED HERE, deliberately: the list still comes from an org-wide
 * fetchDamageReports() narrowed in the browser, damage mutations still write no
 * audit row, and a report still says nothing about HOW MANY pieces broke. All
 * three are pinned by S6a and all three are out of this slice.
 */
import React from "react";

import PhotoGallery from "../../../components/inventory/PhotoGallery";
import { normalizeUnit } from "../../../api/adminData";
import { formatDateOnly } from "../../../utils/datetime";
import { getUnitAddressSubtitle, getUnitHeadingLabel } from "../../../utils/unitDisplayId";

/** CHF with the Swiss thousands separator. Moved verbatim from the page. */
/** S8: two decimals, like the list and the CSV — "CHF 120.00", not "CHF 120". */
function formatChf(value) {
  if (value == null || value === "") return "—";
  return `CHF ${Number(value).toLocaleString("de-CH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function damageStatusLabelDe(status) {
  const s = String(status || "").toLowerCase();
  if (s === "reported") return "Gemeldet";
  if (s === "repair_scheduled") return "Reparatur geplant";
  if (s === "repaired") return "Repariert";
  if (s === "written_off") return "Abgeschrieben";
  return status || "—";
}

/**
 * Tone only. The mapping is the one the page already had — reported reads as
 * attention, repair_scheduled as pending, repaired as done, written_off as
 * neutral — restated in tokens that survive light mode without an !important
 * layer. No status gains or loses meaning here.
 */
function damageStatusBadgeClass(status) {
  const s = String(status || "").toLowerCase();
  if (s === "reported") {
    return "border-red-500/25 bg-red-500/10 text-red-700 dark:text-red-400";
  }
  if (s === "repair_scheduled") {
    return "border-amber-500/25 bg-amber-500/10 text-amber-700 dark:text-amber-400";
  }
  if (s === "repaired") {
    return "border-emerald-500/25 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400";
  }
  return "border-border bg-muted text-muted-foreground";
}

/** Select options: current + allowed transitions for MVP workflow. */
function damageStatusSelectOptions(current) {
  const s = String(current || "").toLowerCase();
  const mk = (value) => ({ value, label: damageStatusLabelDe(value) });
  const cur = mk(s);
  if (s === "reported") {
    return [cur, mk("repair_scheduled"), mk("repaired"), mk("written_off")];
  }
  if (s === "repair_scheduled") {
    return [cur, mk("repaired"), mk("written_off")];
  }
  return [cur];
}

/**
 * One labelled fact. The value is the <dd>'s only child so a lone em dash stays
 * a single matchable node, exactly as on the assignment cards.
 */
function DamageFact({ label, value, children }) {
  return (
    <div className="min-w-0">
      <dt className="text-[9.5px] font-medium uppercase tracking-[0.09em] text-muted-foreground">
        {label}
      </dt>
      <dd className="m-0 mt-0.5 break-words text-[12.5px] font-medium text-foreground">
        {children != null ? children : value}
      </dd>
    </div>
  );
}

function DamageCard({
  report,
  unitLine,
  unitSubtitle,
  roomLine,
  photos,
  uploading,
  busy,
  statusUpdating,
  onUploadPhotos,
  onDeletePhoto,
  onCaptionSave,
  onStatusChange,
}) {
  const opts = damageStatusSelectOptions(report.damage_status);
  return (
    <li className="rounded-[10px] border border-border bg-muted/40 p-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
          <span
            className={`inline-flex whitespace-nowrap rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.09em] ${damageStatusBadgeClass(
              report.damage_status
            )}`}
          >
            {damageStatusLabelDe(report.damage_status)}
          </span>
          <span className="font-mono text-[11px] font-medium tabular-nums text-muted-foreground">
            {formatDateOnly(report.reported_at)}
          </span>
        </div>
        {opts.length > 1 ? (
          <select
            aria-label="Schaden-Status ändern"
            disabled={statusUpdating}
            value={String(report.damage_status || "").toLowerCase()}
            onChange={(e) => onStatusChange(report.id, e.target.value, report.damage_status)}
            className="max-w-[200px] shrink-0 rounded-[8px] border border-border bg-card px-2 py-1.5 text-[12px] font-medium text-foreground outline-none transition-colors focus:border-ring focus:ring-2 focus:ring-ring disabled:opacity-50"
          >
            {opts.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        ) : null}
      </div>

      {/* S7b visual polish: the description gets a readable measure instead of
          running the full width of a 1700px workspace. */}
      <p className="m-0 mt-2.5 max-w-[95ch] whitespace-pre-wrap break-words text-[13px] font-medium text-foreground">
        {report.description || "—"}
      </p>

      {/* S7b visual polish: three EQUAL full-width columns put Zuordnung at the
          far left, Room somewhere in the middle and the cost at the far right
          with dead space after it. The grid is bounded now and weighted so the
          Zuordnung column (unit line plus address) gets the room it needs,
          which keeps the three facts readable as one block. */}
      <dl className="m-0 mt-3 grid grid-cols-2 gap-x-6 gap-y-2.5 sm:max-w-[780px] sm:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)_minmax(0,1fr)]">
        <DamageFact label="Zuordnung">
          {unitLine}
          {unitSubtitle ? (
            <span className="mt-0.5 block text-[11px] font-medium leading-snug text-muted-foreground">
              {unitSubtitle}
            </span>
          ) : null}
        </DamageFact>
        {/* "Room", not "Raum": that is the word on screen today, and changing
            visible copy is a product decision, not a restyle. Flagged for S8. */}
        <DamageFact label="Room" value={roomLine} />
        <DamageFact label="Geschätzte Kosten" value={formatChf(report.estimated_cost_chf)} />
      </dl>

      <div className="mt-3 border-t border-border pt-3">
        <PhotoGallery
          compact
          photos={photos}
          onUpload={(files) => onUploadPhotos(report.id, files)}
          onDelete={(photoId) => onDeletePhoto(report.id, photoId)}
          onCaptionSave={(photoId, caption) => onCaptionSave(report.id, photoId, caption)}
          uploading={uploading}
          busy={busy}
          maxPhotos={10}
          emptyHint="Keine Fotos."
        />
      </div>
    </li>
  );
}

export default function InventoryDamageSection({
  reports,
  loading,
  error,
  assignmentById,
  unitById,
  photosByDamageId,
  uploadingId,
  busyId,
  statusUpdatingId,
  onUploadPhotos,
  onDeletePhoto,
  onCaptionSave,
  onStatusChange,
}) {
  return (
    <section className="rounded-[10px] border border-border bg-card p-4" aria-label="Schäden">
      <h2 className="m-0 text-[10px] font-bold uppercase tracking-[0.09em] text-muted-foreground">
        Schäden
      </h2>
      {error ? <p className="m-0 mt-2 text-[13px] font-medium text-destructive">{error}</p> : null}
      {loading ? (
        <p className="m-0 mt-3 text-[13px] font-medium text-muted-foreground">Lade Schäden …</p>
      ) : reports.length === 0 ? (
        <p className="m-0 mt-3.5 rounded-[8px] border border-dashed border-border bg-muted px-3 py-6 text-center text-[13px] font-medium text-muted-foreground">
          Keine Schäden erfasst.
        </p>
      ) : (
        <ul className="m-0 mt-3.5 flex list-none flex-col gap-2 p-0">
          {reports.map((r) => {
            const asg = assignmentById.get(String(r.assignment_id));
            const u0 = asg ? unitById.get(String(asg.unit_id)) : null;
            /* The second normalizeUnit() is not redundant by accident: the page
               built unitById from normalized rows and the damage block called
               it again, so the move keeps the call rather than assuming the
               helper is idempotent. */
            const nu = u0 ? normalizeUnit(u0) : null;
            return (
              <DamageCard
                key={r.id}
                report={r}
                unitLine={nu ? getUnitHeadingLabel(nu) : "—"}
                unitSubtitle={nu ? getUnitAddressSubtitle(nu) : ""}
                roomLine={asg?.room_name || "—"}
                photos={photosByDamageId[r.id] || []}
                uploading={uploadingId === String(r.id)}
                busy={busyId === String(r.id)}
                statusUpdating={statusUpdatingId === String(r.id)}
                onUploadPhotos={onUploadPhotos}
                onDeletePhoto={onDeletePhoto}
                onCaptionSave={onCaptionSave}
                onStatusChange={onStatusChange}
              />
            );
          })}
        </ul>
      )}
    </section>
  );
}
